<!-- DAILY EXHAUSTIN — one puzzle a day, one attempt, and a streak to protect.

     The puzzle is DERIVED FROM THE DATE (see game/daily.js), so closing the app
     and reopening it cannot re-roll a bad draw, and it needs no server, which
     is what lets it work with no signal.

     Three out of five keeps the streak. Perfection is not the price of coming
     back tomorrow. -->
<template>
  <IonPage>
    <AppHeader title="Daily" />

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Daily" />

      <div class="ex-total">
        <div>
          <div class="k">Day streak</div>
          <div class="n tabular">{{ profile.dailyStreak ?? 0 }}</div>
        </div>
        <div class="center">
          <div class="k">Today</div>
          <div class="n" style="font-size: var(--text-lg)">{{ plan.modeName }}</div>
        </div>
      </div>

      <!-- Already had his go: the streak is the payoff, not a second attempt. -->
      <template v-if="doneToday && !playing">
        <EmptyState
          glyph="✅"
          title="That's today done."
          :body="`Streak: ${profile.dailyStreak ?? 0}. A fresh one lands tomorrow — one puzzle, one attempt, same as this.`"
          cta="Play something else"
          @act="$router.push('/tabs/play')"
        />
      </template>

      <template v-else-if="!playing">
        <div class="pad">
          <p class="ex-sub" style="margin-top:0">
            Five questions drawn from <strong>{{ plan.modeName }}</strong>. One
            attempt, and it is the same puzzle for everyone all day. Get three
            right to keep the streak.
          </p>
        </div>
        <div class="pad" style="margin-top: var(--sp-4)">
          <IonButton expand="block" size="large" @click="begin">Take today's go</IonButton>
        </div>
      </template>

      <template v-else-if="current">
        <div ref="stageRef" tabindex="-1">
          <GameHud :score="score" :streak="streak" label="Question" :value="`${idx + 1}/${plan.questions.length}`" />
          <QuickfireBoard :question="current" :revealed="revealed" :chosen="chosen" @answer="answer" />
        </div>
      </template>

      <div class="tail" />
    </IonContent>

    <VerdictFlash :text="verdict.text" :why="verdict.why" :good="verdict.good" />
    <AusBonus :when="ausNow" @done="ausNow = false" />
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { IonPage, IonContent, IonButton } from '@ionic/vue';
import { AppHeader, AppLargeTitle, announce } from '../core/ui';
import GameHud from '../components/GameHud.vue';
import VerdictFlash from '../components/VerdictFlash.vue';
import AusBonus from '../components/AusBonus.vue';
import QuickfireBoard from '../components/QuickfireBoard.vue';
import EmptyState from '../components/EmptyState.vue';
import { dailyPlan, todayKey, nextStreak, playedToday, DAILY_PASS_MARK } from '../game/daily.js';
import { pointsFor } from '../game/engine.js';
import { profile, ensureProfile } from '../game/profile.js';
import { addDailyPuzzle } from '../store.js';
import { finishRun } from '../game/run.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();

const plan = ref(dailyPlan());
const playing = ref(false);
const idx = ref(0);
const revealed = ref(false);
const chosen = ref(-1);
const ausNow = ref(false);
const stageRef = ref(null);

const score = ref(0);
const streak = ref(0);
const bestChain = ref(0);
const correct = ref(0);
const wrong = ref(0);
const ausCount = ref(0);
const verdict = ref({ text: '', why: '', good: true });

const doneToday = computed(() => playedToday(profile.value.lastDailyOn));
const current = computed(() => plan.value.questions[idx.value] ?? null);

let advanceTimer = null;
let ended = false;

function begin() {
  playing.value = true;
  // The "Take today's go" button is gone the instant this runs, so without
  // this a keyboard or screen-reader user is dropped back to <body>.
  nextTick(() => stageRef.value?.focus());
}

function answer(i) {
  if (revealed.value || ended || !current.value) return;
  const q = current.value;
  revealed.value = true;
  chosen.value = i;

  if (i === q.answer) {
    if (q.aus) { ausCount.value += 1; ausNow.value = true; }
    score.value += pointsFor({ difficulty: q.difficulty, streak: streak.value, aus: q.aus });
    streak.value += 1;
    bestChain.value = Math.max(bestChain.value, streak.value);
    correct.value += 1;
    haptic('success');
    verdict.value = { text: pun('correct'), why: '', good: true };
  } else {
    streak.value = 0;
    wrong.value += 1;
    haptic('error');
    verdict.value = { text: pun('wrong'), why: q.reason, good: false };
  }

  clearTimeout(advanceTimer);
  advanceTimer = setTimeout(next, 1700);
}

function next() {
  if (ended) return;
  verdict.value = { text: '', why: '', good: true };
  revealed.value = false;
  chosen.value = -1;
  idx.value += 1;
  if (idx.value >= plan.value.questions.length) { end(); return; }
  announce(current.value?.prompt ?? '');
}

async function end() {
  if (ended) return;
  ended = true;
  clearTimeout(advanceTimer);

  const survived = correct.value >= DAILY_PASS_MARK;
  const streakNow = nextStreak({
    lastDailyOn: profile.value.lastDailyOn,
    dailyStreak: profile.value.dailyStreak,
    survived,
  });

  // The plan's daily-puzzle record: a receipt that today's go was taken, which
  // is what makes "one attempt" survive a reinstall of the page.
  addDailyPuzzle({
    onDate: todayKey(),
    mode: plan.value.mode,
    question: plan.value.questions[0]?.id ?? '',
    attempted: true,
    outcome: survived ? 'survived' : 'exhausted',
  });

  await finishRun({
    mode: 'daily',
    score: score.value,
    lastedMs: 0,
    correct: correct.value,
    wrong: wrong.value,
    bestChain: bestChain.value,
    ausBonuses: ausCount.value,
    dailyStreak: streakNow,
    lastDailyOn: todayKey(),
  });

  router.replace('/tabs/daily/results');
}

onMounted(ensureProfile);
</script>
